import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const ease = [0.22, 1, 0.36, 1];

const items = [
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_ad-network-hub-3/artifacts/mk9tl7x1_image.png",
    venue: "Skyline Golf",
    location: "Delhi NCR",
    objectPosition: "center",
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_ad-network-hub-3/artifacts/kbtx0flm_image.png",
    venue: "Hamoni Golf",
    location: "Delhi NCR",
    objectPosition: "center",
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_ad-network-hub-3/artifacts/8zwv9vef_eff1ff44-ec3c-4d93-94f7-cb121991a160.png",
    venue: "Classic Golf & Country Club",
    location: "Delhi NCR",
    objectPosition: "center",
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_ad-network-hub-3/artifacts/n7db673p_image.png",
    venue: "Golden Greens",
    location: "Delhi NCR",
    objectPosition: "center",
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_ad-network-hub-3/artifacts/ndk30ikd_c42cd9c0-2697-4e26-9744-8d528cbca190.png",
    venue: "Panchkula Golf Club",
    location: "Panchkula",
    objectPosition: "center",
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_ad-network-hub-3/artifacts/dtfquols_56a44668-9b48-4a59-bc47-f20db5803d21.png",
    venue: "Panchkula Golf Club",
    location: "Panchkula",
    objectPosition: "center",
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_ad-network-hub-3/artifacts/2ewirr7r_image.png",
    venue: "Chandigarh Golf Club",
    location: "Chandigarh",
    objectPosition: "center 35%",
    beautify: true,
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_ad-network-hub-3/artifacts/xjou8xo3_image.png",
    venue: "CGA Range",
    location: "Chandigarh",
    objectPosition: "center 30%",
    beautify: true,
  },
];

const Showcase = () => {
  return (
    <section
      id="showcase"
      data-testid="showcase-section"
      className="relative py-28 md:py-36 bg-ivory"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 md:mb-20 items-end">
          <div className="md:col-span-7">
            <h2 className="h-display text-4xl md:text-6xl">
              Live from the network,
              <br />
              <span className="italic text-brass">real placements</span>, real venues.
            </h2>
          </div>
          <div className="md:col-span-5 md:pl-8 md:border-l md:border-forest-800/15">
            <p className="text-forest-600 text-base md:text-lg leading-relaxed font-light">
              Outdoor LED placements designed for the fairway, the lounge and
              every quiet moment of attention in between.
            </p>
          </div>
        </div>

        {/* Gallery wall */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {items.map((it, i) => (
            <motion.figure
              key={it.venue + i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.9, ease, delay: i * 0.07 }}
              className="group relative"
              data-testid={`showcase-card-${i}`}
            >
              {/* Frame */}
              <div className="relative shadow-luxe-hover hairline bg-ivory-soft p-3 md:p-4">
                {/* Corner gold marks */}
                <span className="pointer-events-none absolute top-2 left-2 w-4 h-px bg-brass/80" />
                <span className="pointer-events-none absolute top-2 left-2 w-px h-4 bg-brass/80" />
                <span className="pointer-events-none absolute top-2 right-2 w-4 h-px bg-brass/80" />
                <span className="pointer-events-none absolute top-2 right-2 w-px h-4 bg-brass/80" />
                <span className="pointer-events-none absolute bottom-2 left-2 w-4 h-px bg-brass/80" />
                <span className="pointer-events-none absolute bottom-2 left-2 w-px h-4 bg-brass/80" />
                <span className="pointer-events-none absolute bottom-2 right-2 w-4 h-px bg-brass/80" />
                <span className="pointer-events-none absolute bottom-2 right-2 w-px h-4 bg-brass/80" />

                {/* Image plate (portrait, ~3:4) */}
                <div className="relative aspect-[3/4] overflow-hidden bg-forest-900/5">
                  <img
                    src={it.src}
                    alt={`${it.venue} — ${it.location}`}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
                    style={{
                      objectPosition: it.objectPosition || "center",
                      imageRendering: "auto",
                      filter: it.customFilter
                        ? it.customFilter
                        : it.beautify
                        ? "contrast(1.07) saturate(1.10) brightness(1.02)"
                        : "contrast(1.03) saturate(1.04)",
                      transform: it.zoom ? `scale(${it.zoom})` : undefined,
                      transformOrigin: it.zoom ? "center top" : undefined,
                    }}
                    loading="lazy"
                  />
                  {/* Subtle gradient at very bottom of image only */}
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-forest-900/25 to-transparent" />

                  {/* Hover arrow */}
                  <span className="absolute bottom-3 right-3 w-8 h-8 hairline border-ivory/40 text-ivory bg-forest-900/40 backdrop-blur-sm flex items-center justify-center transition-all duration-500 group-hover:bg-brass group-hover:border-brass group-hover:text-forest-900">
                    <ArrowUpRight size={14} strokeWidth={1.6} />
                  </span>
                </div>
              </div>

              {/* Venue plate — museum-wall style */}
              <figcaption className="mt-4 px-1">
                <div className="text-[10px] tracking-[0.28em] uppercase text-brass truncate">
                  {it.location}
                </div>
                <div className="font-serif text-lg md:text-xl text-forest-900 mt-1 leading-tight">
                  {it.venue}
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>

        {/* Site-visit CTA strip */}
        <div className="mt-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 hairline shadow-luxe px-8 py-6 bg-ivory-soft">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-brass">
              Site Visit
            </div>
            <div className="font-serif text-2xl text-forest-900 mt-1">
              See a screen in action.
            </div>
          </div>
          <button
            onClick={() => {
              const el = document.querySelector("#contact");
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }}
            className="btn-gold"
            data-testid="showcase-cta"
          >
            Request a Visit
            <ArrowUpRight size={16} strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Showcase;
